import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import Materials from "./Materials";
import Timing from "./Timing";
import CloseInAdvance from "./CloseInAdvance";
import Irregularities from "./Irregularities";
import AcceptReview from "./AcceptReview";
import TaskManagementService from "../../TaskManagementService";
import { ValidationService } from "shared/services";
import * as rules from "shared/validations";
import { find, isUndefined, isEmpty } from "lodash";
import { IrregularityModel } from "features/irregularities";
import {
  AlertNotice,
  Spinner,
  ExamineeStatusTabs,
  ModalInfo,
  SimpleModal,
} from "shared/components";
import { isRoomOnline } from "shared/helpers";
import { useParams } from "react-router-dom";
import { ROOM_STATUS } from "shared/constants";
import PropTypes from "prop-types";

const ReviewAndCloseRoomModal = ({
  roomId,
  roomAvailableTasks,
  completedTasks,
  user,
  irregularityTypes,
  actions,
  room,
  reviewAndCloseModalOpen,
  closeReviewAndCloseModal,
  roomStatus,
  manualTimerFlag,
  testCenterCycle,
}) => {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const [activeTab, setActiveTab] = useState("unknown");
  const isClosed = roomStatus.status === ROOM_STATUS.closed;
  const [loadingData, setLoadingData] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [timing, setTiming] = useState([]);
  const [statusSummary, setStatusSummary] = useState({});
  const [examinees, setExaminees] = useState([]);
  const [irregularities, setIrregularities] = useState([]);
  const closeInAdvance = roomAvailableTasks?.length !== completedTasks?.length;
  const [checked, setChecked] = useState(
    isClosed && room.roomType && room.roomType.trackMaterialFlag
  );
  const [hasError, setHasError] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [inputValue, setInputValue] = useState({
    scored: !isUndefined(room.answerDocScored)
      ? room.answerDocScored.toString()
      : "",
    voided: !isUndefined(room.answerDocVoided)
      ? room.answerDocVoided.toString()
      : "",
    replaced: !isUndefined(room.answerDocReplaced)
      ? room.answerDocReplaced.toString()
      : "",
  });
  const [textAreaValue, setTextAreaValue] = useState({ irregularities: "" });
  const initialErrors = {};
  const [textAreaError, setTextAreaError] = useState({ irregularities: "" });
  const [errors, setErrors] = useState(initialErrors);
  const [errorMessage, setErrorMessage] = useState("");
  const [serverError, setServerError] = useState("");
  const [isDiscardModal, setIsDiscardModal] = useState(false);

  // useEffect(() => {
  //   (async () => {
  //     if (roomStatus.status !== ROOM_STATUS.inactive) {
  //       fetchData();
  //     }
  //   })();
  // }, [roomStatus.status]);

  useEffect(() => {
    if (loadingData) return;
    Array.from(document.querySelectorAll('[data-bs-toggle="popover"]')).forEach(
      (el) => bootstrap.Popover.getOrCreateInstance(el).enable()
    );
  }, [loadingData]);

  useEffect(() => {
    if (!errors.scored && !errors.voided) {
      setErrorMessage("");
    }
  }, [errors]);

  useEffect(() => {
    (async () => {
      fetchData();
    })();
  }, [activeTab]);

  useEffect(() => {
    if (
      !isRoomOnline(room.roomType) &&
      inputValue.scored &&
      inputValue.voided &&
      !isEmpty(statusSummary)
    ) {
      handleMessage();
    }
  }, [inputValue, statusSummary]);

  useEffect(() => {
    if (reviewAndCloseModalOpen) {
      fetchData();

      setInputValue({
        scored: !isUndefined(room.answerDocScored)
          ? room.answerDocScored.toString()
          : "",
        voided: !isUndefined(room.answerDocVoided)
          ? room.answerDocVoided.toString()
          : "",
        replaced: !isUndefined(room.answerDocReplaced)
          ? room.answerDocReplaced.toString()
          : "",
      });
    }

    if (!reviewAndCloseModalOpen) {
      setErrors(initialErrors);
      setChecked(isClosed && room.roomType && room.roomType.trackMaterialFlag);
      setHasError(false);
      setTextAreaError({ irregularities: "" });
      setServerError("");
      setSuccessMessage("");
      setTextAreaValue({ irregularities: "" });
    }
  }, [reviewAndCloseModalOpen]);

  const fetchData = async () => {
    setLoadingData(true);
    const materialSummary = await TaskManagementService.getRoomMaterialSummary(
      roomId
    );
    const testedStatusSummary =
      await TaskManagementService.getRoomTestedStatusSummary(roomId);
    const roomTestedStatus = await TaskManagementService.getRoomTestedStatusReg(
      roomId,
      activeTab
    );
    const irregularitiesList =
      await TaskManagementService.getRoomIrregularities(roomId);
    const time = await TaskManagementService.getTiming(roomId);

    setMaterials(materialSummary.data);
    setStatusSummary(testedStatusSummary);
    setExaminees(roomTestedStatus.data);
    setIrregularities(irregularitiesList);
    setTiming(time.data);
    setLoadingData(false);
  };

  const handleInputChange = (e) => {
    setInputValue({
      ...inputValue,
      [e.target.name]: e.target.value,
    });
    setErrors({
      ...errors,
      [e.target.name]: "",
    });
    setServerError("");

    if (isClosed && checked) {
      setChecked(false);
    }
    if (Number(e.target.value) > statusSummary.tested) {
      setErrorMessage(
        t("reviewAndCloseRoom.examineeStatus.answerError")
      );
      setErrors({
        ...errors,
        scored: " ",
        voided: " ",
      });
    }
    if (e.target.name !== "replaced" && e.target.value.trim() === "") {
      setSuccessMessage("");
    }
  };

  const handleTextAreaChange = (e) => {
    setTextAreaValue({ irregularities: e.target.value });
    setTextAreaError({
      irregularities: "",
    });
    setServerError("");
  };

  const handleMessage = () => {
    if (
      Number(inputValue.scored) + Number(inputValue.voided) ===
      statusSummary.tested
    ) {
      setSuccessMessage(
        t("reviewAndCloseRoom.examineeStatus.answerMatch")
      );
      setErrorMessage("");
      setErrors({
        ...errors,
        scored: "",
        voided: "",
      });
      return false;
    } else {
      setErrorMessage(
        t("reviewAndCloseRoom.examineeStatus.answerError")
      );
      setSuccessMessage("");
      setErrors({
        ...errors,
        scored: " ",
        voided: " ",
      });
      return true;
    }
  };

  const submitIrreg = async () => {
    const typeId = find(irregularityTypes, {
      type: "room_closed_in_advance",
    }).id;
    const irregularity = new IrregularityModel({
      testCenterEventId: eventId,
      scope: "room",
      notes: textAreaValue.irregularities,
      submitPersonnel: { id: user.id },
      irregularityRooms: [{ roomId }],
      irregularityType: {
        id: typeId,
      },
      submitDateTime: new Date(),
      startDateTime: new Date(),
      duration: 1,
    });
    await actions.setIrregularity(irregularity);
  };

  const handleSubmit = async () => {
    let requiredErrors;
    let requiredTextAreaError;
    let hasDocErrors;

    if (materials.length > 0) {
      requiredErrors = ValidationService.validate(
        inputValue,
        rules.documentsRules,
        rules.documentsErrorStrings
      );
      setErrors({
        ...errors,
        ...requiredErrors,
      });
      if (isEmpty(requiredErrors) && checked) {
        hasDocErrors = handleMessage();
      }
    }

    if (closeInAdvance && !isClosed && !manualTimerFlag) {
      requiredTextAreaError = ValidationService.validate(
        textAreaValue,
        rules.closeRoomIrregularityRules,
        rules.documentsErrorStrings
      );
      setTextAreaError(requiredTextAreaError);
    }

    if (errors && !isEmpty(errors)) {
      if (!errorMessage) {
        setErrorMessage(
          t("reviewAndCloseRoom.examineeStatus.requiredMessage")
        );
      }
    }

    if (!checked) {
      setHasError(true);
    }

    const hasErrors = Object.values(errors).some((x) => x);
    if (
      isEmpty(requiredErrors) &&
      isEmpty(requiredTextAreaError) &&
      !hasErrors &&
      checked &&
      !hasDocErrors
    ) {
      const roomNewProps = {
        answerDocVoided: Number(inputValue.voided),
        answerDocReplaced: Number(inputValue.replaced),
        answerDocScored: Number(inputValue.scored),
      };
      if (closeInAdvance && !isClosed && !manualTimerFlag) {
        submitIrreg();
      }
      const response = await actions.updateRoomStatus(roomId, {
        status: ROOM_STATUS.closed,
      });
      if (response && !isEmpty(response.data) && response.data.message) {
        setServerError(
          response.data.message || t("errors.somethingWentWrong")
        );
        return;
      } else {
        actions.setRoomStatusAction({
          data: {
            status: ROOM_STATUS.closed,
          },
        });
        actions.updateRoom({ ...room, ...roomNewProps });
        closeReviewAndCloseModal(true);
      }
      actions.setRoomStatusAction({
        data: {
          status: ROOM_STATUS.closed,
        },
      });
      const updatedRoom = await actions.updateRoom({
        ...room,
        ...roomNewProps,
      });
      closeReviewAndCloseModal(true);
      // if ( (updatedRoom && !isEmpty(updatedRoom.data) && updatedRoom.data.message) || !updatedRoom) {
      //   setServerError(t('errors.somethingWentWrong'));
      // } else {

      // }
    }
  };

  const handleCLoseModal = () => {
    const hasMaterials = Object.values(inputValue).some((value) => !!value);
    const hasIrregularities = Object.values(textAreaValue).some(
      (value) => !!value
    );

    if (checked || hasMaterials || hasIrregularities) {
      setIsDiscardModal(true);
    } else {
      closeReviewAndCloseModal(false);
    }
  };

  const handleClosedRoomModal = () => {
    if (
      room.answerDocReplaced !== Number(inputValue.replaced) ||
      room.answerDocScored !== Number(inputValue.scored) ||
      room.answerDocVoided !== Number(inputValue.voided) ||
      !checked
    ) {
      setIsDiscardModal(true);
    } else {
      closeReviewAndCloseModal(false);
    }
  };

  const handleCloseDiscardModal = () => {
    closeReviewAndCloseModal(false);
    setIsDiscardModal(false);
  };

  return (
    <>
      <ModalInfo
        containerClass="review-close-room-modal"
        modalIsOpen={reviewAndCloseModalOpen}
        closeModal={isClosed ? handleClosedRoomModal : handleCLoseModal}
        headerTitle={t(
          "reviewAndCloseRoom.examineeStatus.reviewAndClose"
        )}
        onSubmit={handleSubmit}
        showCancel
        cancelLabel={t("common.labels.back")}
        saveLabel={t("common.labels.submit")}
        headerSticky
        fullHeightModal
        headerBack
        loadingButton={loadingData}
        className="review-and-close-room"
      >
        {loadingData && <Spinner hideText noHeight />}
        <div className={`${loadingData ? "loading-data" : ""}`}>
          {closeInAdvance && !isClosed && !manualTimerFlag && (
            <CloseInAdvance
              handleInputChange={handleTextAreaChange}
              inputValue={textAreaValue}
              errors={textAreaError}
            />
          )}
          <ExamineeStatusTabs
            statusSummary={statusSummary}
            examinees={examinees}
            setActiveTab={setActiveTab}
            tabs={["unknown", "tested"]}
            seatingChartRequiredFlag={
              testCenterCycle?.cycle?.seatingChartRequiredFlag
            }
          />
          {materials.length > 0 && (
            <Materials
              materials={materials}
              handleInputChange={handleInputChange}
              inputValue={inputValue}
              errors={errors}
              errorMessage={errorMessage}
              successMessage={successMessage}
            />
          )}
          <Irregularities irregularities={irregularities} />
          {timing.length > 0 && <Timing timing={timing} />}
          <AcceptReview
            checked={checked}
            setChecked={setChecked}
            hasError={hasError}
            setHasError={setHasError}
            setServerError={setServerError}
          />
          {serverError && (
            <div className="mb-3">
              <AlertNotice message={serverError} type="error" />
            </div>
          )}
        </div>
      </ModalInfo>
      <SimpleModal
        isOpen={isDiscardModal}
        onCancel={() => setIsDiscardModal(false)}
        onConfirm={handleCloseDiscardModal}
        confirmLabel={t("common.labels.yes")}
        cancelLabel={t("common.labels.no")}
        headline={t("reviewAndCloseRoom.examineeStatus.discardTitle")}
        message={t(
          "reviewAndCloseRoom.examineeStatus.discardMessage"
        )}
        buttonsReverseOrder
      />
    </>
  );
};

export default ReviewAndCloseRoomModal;

ReviewAndCloseRoomModal.propTypes = {
  roomAvailableTasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  completedTasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  roomId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  reviewAndCloseModalOpen: PropTypes.bool.isRequired,
  closeReviewAndCloseModal: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  irregularityTypes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  actions: PropTypes.shape({
    setRoomStatusAction: PropTypes.func.isRequired,
    updateRoom: PropTypes.func.isRequired,
    updateRoomStatus: PropTypes.func.isRequired,
    setIrregularity: PropTypes.func.isRequired,
  }).isRequired,
  room: PropTypes.shape({}).isRequired,
  seatingChartRequiredFlag: PropTypes.bool,
};
